import React, { useCallback, useMemo, useState } from 'react';
import { compose } from 'redux';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { View, RefreshControl, ScrollView } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import Toast from 'react-native-root-toast';
import moment from 'moment';
import { createMaterialTopTabNavigator } from '@react-navigation/material-top-tabs';
import { useFocusEffect, useNavigation } from '@react-navigation/native';

import { DetailContainer } from '../../../containers';
import { useRefreshPage } from '../../../hooks/useRefreshPage';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { Text, Button, Dialog } from '../../../components';
import { SegmentedTabBar } from '../../../design/components';
import PageHeading from '../../../design/components/PageHeading';
import SetupSection from '../components/SetupSection';
import SetupInfoRow from '../components/SetupInfoRow';
import SetupDateField from '../components/SetupDateField';
import SetupSwitchField from '../components/SetupSwitchField';
import SetupAssigneePicker from '../components/SetupAssigneePicker';
import MachineSetupList from '../machine/MachineSetupList';
import MarketSetupList from '../market/MarketSetupList';
import { GET_MACHINE_SETUPS } from '../../../../common/graphql/machineSetup';
import { GET_MARKET_SETUPS } from '../../../../common/graphql/marketSetup';
import { STATUS } from '../../../../common/constants';
import { flattenObjectDataInArray } from '../../../../common/utils/general/flattenObjectData';
import { addressToString } from '../../../../common/utils/address';
import { PersonSelector } from '../../../components/selector';

const Tab = createMaterialTopTabNavigator();

const renderTabBar = props => <SegmentedTabBar {...props} />;

const buildLocationSetupEmailTemplate = (locationSetup) => {
	const formattedDate = locationSetup?.dueDate ? moment(locationSetup.dueDate).format('YYYY-MMM-DD') : 'Not set';
	const locationName = locationSetup?.location?.name || 'Unknown';
	const locationAddress = addressToString(locationSetup?.location?.addressObj || {}) || 'Unknown';
	const machineSetups = locationSetup?.machineSetups || [];

	const lines = [
		'Hello,',
		'',
		`Can we please book the following move? We were hoping for delivery by: ${formattedDate}`,
		'',
		'The details are as follows:',
		'',
		`Location name: ${locationName}`,
		`Location address: ${locationAddress}`,
	];

	if (machineSetups.length > 1) {
		lines.push('');
		lines.push(`Number of machines for delivery: ${machineSetups.length}`);
	}

	if (machineSetups.length) {
		lines.push('');
		if (machineSetups.length > 1) {
			lines.push('Machines:');
		}

		machineSetups.forEach((machineSetup) => {
			const assignedMachine = machineSetup?.machine || machineSetup?.plannedMachine;
			const source = [machineSetup?.moveSourceLocation?.name, machineSetup?.moveSourcePlace?.name]
				.filter(Boolean)
				.join(' — ');
			lines.push(`Machine place: ${machineSetup?.place?.name || 'Unknown'}`);
			lines.push(`Machine model: ${machineSetup?.machineModel?.name || 'Unknown'}`);
			lines.push(`Machine ID: ${assignedMachine?.assetId || 'Unknown'}`);
			if (machineSetup?.movePlanId && source) {
				lines.push(`Current source: ${source}`);
			}
			lines.push('');
		});
	}

	if (lines[lines.length - 1] !== '') {
		lines.push('');
	}
	lines.push(`Delivery contact name: ${locationSetup?.deliveryContact?.name || 'Not set'}`);
	lines.push(`Delivery contact phone: ${locationSetup?.deliveryContact?.phoneNumber || 'Not set'}`);
	lines.push(`Delivery contact email: ${locationSetup?.deliveryContact?.email || 'Not set'}`);
	lines.push(`Delivery contact role: ${locationSetup?.deliveryContact?.role || 'Not set'}`);
	lines.push('');

	lines.push('Regards,');

	return lines.join('\n');
};

const LocationSetupInformationTab = ({ locationSetup, onUpdate, refreshPage, isRefreshing }) => {
	const styles = useThemedStyles(createStyles);
	const [isEmailTemplateOpen, setIsEmailTemplateOpen] = useState(false);
	const hasMachineSetups = Boolean(locationSetup?.machineSetups?.length);
	const isDeliveryContactMissing = !locationSetup?.deliveryContact || locationSetup.deliveryContact.destroyed;
	const disableTransport = locationSetup?.status === STATUS.PENDING || isDeliveryContactMissing;
	const baseTransportHint = 'Simply check this checkbox once machine transport has been booked for this location.';
	const transportHint = locationSetup?.status === STATUS.PENDING
		? `${baseTransportHint}\nComplete the setups, and then book transport.`
		: (isDeliveryContactMissing
			? `${baseTransportHint}\nSelect a delivery contact before booking transport.`
			: baseTransportHint);
	const emailTemplate = useMemo(() => buildLocationSetupEmailTemplate(locationSetup), [locationSetup]);
	const handleSubscribersChange = useCallback((ids) => {
		onUpdate({ subscribers: flattenObjectDataInArray(ids) });
	}, [onUpdate]);
	const handleCopyEmailTemplate = useCallback(async () => {
		try {
			await Clipboard.setStringAsync(emailTemplate);
			Toast.show('Email content copied to clipboard');
		}
		catch (error) {
			Toast.show('Unable to copy email content');
		}
	}, [emailTemplate]);

	return (
		<>
			<KeyboardAwareScrollView
				style={styles.infoScrollView}
				contentContainerStyle={styles.infoContent}
				scrollIndicatorInsets={{ right: 1 }}
				enableOnAndroid={true}
				refreshControl={(
					<RefreshControl
						refreshing={isRefreshing}
						onRefresh={refreshPage}
					/>
				)}
			>
				<SetupSection title="Information">
					<SetupInfoRow
						label="Location"
						value={locationSetup.location?.name || 'Unknown'}
					/>
					<SetupInfoRow
						label="Client"
						value={locationSetup.location?.client?.name || 'Unknown'}
					/>
					<SetupDateField
						label="Due date"
						value={locationSetup.dueDate}
						onChange={(date) => onUpdate({ dueDate: date })}
						required={true}
					/>
					<Text style={styles.label}>
						Delivery contact<Text style={styles.required}> *</Text>
					</Text>
					<PersonSelector
						value={locationSetup.deliveryContact}
						onChange={(contact) => onUpdate({ deliveryContact: contact.id })}
						buttonStyle={styles.selectorButton}
					/>
					<SetupInfoRow
						label="Name"
						value={locationSetup.deliveryContact?.name || 'Not set'}
					/>
					<SetupInfoRow
						label="Phone"
						value={locationSetup.deliveryContact?.phoneNumber || 'Not set'}
					/>
					<SetupInfoRow
						label="Email"
						value={locationSetup.deliveryContact?.email || 'Not set'}
					/>
					<SetupInfoRow
						label="Role"
						value={locationSetup.deliveryContact?.role || 'Not set'}
					/>
					<Text style={styles.label}>Subscribers</Text>
					<SetupAssigneePicker
						assignees={locationSetup.subscribers || []}
						onChange={handleSubscribersChange}
						placeholder="Add subscriber"
					/>
					<Text style={styles.helperText}>Select users to subscribe to this location setup.</Text>
				</SetupSection>

				{hasMachineSetups && (
					<SetupSection title="Transport">
						<SetupSwitchField
							label="Machine transport booked"
							value={locationSetup.bookTransport}
							onChange={(value) => onUpdate({ bookTransport: value })}
							hint={transportHint}
							disabled={disableTransport}
						/>
						<SetupSwitchField
							label="Send email"
							value={locationSetup.sendEmail}
							onChange={(value) => onUpdate({ sendEmail: value })}
							hint="Email template to book transport"
						/>
						<Button
							label="Open email template"
							secondary={true}
							style={styles.emailTemplateButton}
							onPress={() => setIsEmailTemplateOpen(true)}
						/>
					</SetupSection>
				)}
			</KeyboardAwareScrollView>
			<Dialog
				isOpen={isEmailTemplateOpen}
				onClose={() => setIsEmailTemplateOpen(false)}
			>
				<View style={styles.emailDialogContent}>
					<Text style={styles.emailDialogTitle}>Email template</Text>
					<ScrollView
						style={styles.emailBodyScroll}
						contentContainerStyle={styles.emailBodyScrollContent}
					>
						<Text selectable={true} style={styles.emailBodyText}>{emailTemplate}</Text>
					</ScrollView>
					<Button
						label="Copy to clipboard"
						onPress={handleCopyEmailTemplate}
						secondary={true}
					/>
				</View>
			</Dialog>
		</>
	);
};

const LocationSetupMachineSetupsTab = ({ locationSetupId, locationSetupSummary }) => {
	const styles = useThemedStyles(createStyles);
	const navigation = useNavigation();
	// Memoize the filter so the underlying ListContainer only refetches when the ID actually changes.
	const filter = useMemo(() => {
		return locationSetupId ? { locationSetup: locationSetupId } : null;
	}, [locationSetupId]);

	const handleOpenCreator = useCallback(() => {
		if (!locationSetupSummary) {
			return;
		}
		navigation.navigate('MachineSetupCreatorView', { locationSetup: locationSetupSummary });
	}, [navigation, locationSetupSummary]);

	if (!filter) {
		return (
			<View style={styles.listWrapper}>
				<Text style={styles.placeholderText}>Location setup not found.</Text>
			</View>
		);
	}

	return (
		<View style={styles.listWrapper}>
			<MachineSetupList
				query={GET_MACHINE_SETUPS}
				initialSortBy="status:1_dueDate:-1"
				initialPerPage={15}
				initialFilter={filter}
				listId={`native_location_setup_machine_setups_${locationSetupId}`}
				customControls={(
					<Button
						label="New machine setup"
						secondary={true}
						style={styles.listButton}
						onPress={handleOpenCreator}
						disabled={!locationSetupSummary}
					/>
				)}
			/>
		</View>
	);
};

const LocationSetupMarketSetupsTab = ({ locationSetupId, locationSetupSummary }) => {
	const styles = useThemedStyles(createStyles);
	const navigation = useNavigation();
	// Memoize the filter so the underlying ListContainer only refetches when the ID actually changes.
	const filter = useMemo(() => {
		return locationSetupId ? { locationSetup: locationSetupId } : null;
	}, [locationSetupId]);

	const handleOpenCreator = useCallback(() => {
		if (!locationSetupSummary) {
			return;
		}
		navigation.navigate('MarketSetupCreatorView', { locationSetup: locationSetupSummary });
	}, [navigation, locationSetupSummary]);

	if (!filter) {
		return (
			<View style={styles.listWrapper}>
				<Text style={styles.placeholderText}>Location setup not found.</Text>
			</View>
		);
	}

	return (
		<View style={styles.listWrapper}>
			<MarketSetupList
				query={GET_MARKET_SETUPS}
				initialSortBy="status:1_dueDate:-1"
				initialPerPage={15}
				initialFilter={filter}
				listId={`native_location_setup_market_setups_${locationSetupId}`}
				customControls={(
					<Button
						label="New market setup"
						secondary={true}
						style={styles.listButton}
						onPress={handleOpenCreator}
						disabled={!locationSetupSummary}
					/>
				)}
			/>
		</View>
	);
};

const LocationSetupDetail = ({
	data: locationSetup,
	modifyItem,
	refetch,
}) => {
	const styles = useThemedStyles(createStyles);
	const [refreshPage, isRefreshing] = useRefreshPage(refetch);
	const locationSetupSummary = useMemo(() => {
		if (!locationSetup) {
			return null;
		}
		return {
			id: locationSetup.id,
			identifier: locationSetup.identifier,
			dueDate: locationSetup.dueDate,
			location: locationSetup.location,
		};
	}, [locationSetup]);

	const handleUpdate = useCallback((values) => {
		return modifyItem(values);
	}, [modifyItem]);

	// Refetch when returning from creator screens so tabs remain current.
	useFocusEffect(useCallback(() => {
		if (refetch) {
			refetch();
		}
	}, [refetch]));

	if (!locationSetup) {
		return null;
	}

	return (
		<View style={styles.container}>
			<PageHeading
				title={locationSetup.identifier}
				subtitle={locationSetup.location?.name}
				style={styles.headingContainer}
			/>
			<View style={styles.tabContainer}>
				{/* Split the detail view into tabs so heavy machine/market lists only load when opened. */}
				<Tab.Navigator
					tabBar={renderTabBar}
					screenOptions={{
						sceneStyle: styles.tabScene,
						lazy: true,
					}}
				>
					<Tab.Screen
						name="LocationSetupInformation"
						options={{ tabBarLabel: 'Information' }}
					>
						{() => (
							<LocationSetupInformationTab
								locationSetup={locationSetup}
								onUpdate={handleUpdate}
								refreshPage={refreshPage}
								isRefreshing={isRefreshing}
							/>
						)}
					</Tab.Screen>
					<Tab.Screen
						name="LocationSetupMachineSetups"
						options={{ tabBarLabel: 'Machine setups' }}
					>
						{() => (
							<LocationSetupMachineSetupsTab
								locationSetupId={locationSetup.id}
								locationSetupSummary={locationSetupSummary}
							/>
						)}
					</Tab.Screen>
					<Tab.Screen
						name="LocationSetupMarketSetups"
						options={{ tabBarLabel: 'Market setups' }}
					>
						{() => (
							<LocationSetupMarketSetupsTab
								locationSetupId={locationSetup.id}
								locationSetupSummary={locationSetupSummary}
							/>
						)}
					</Tab.Screen>
				</Tab.Navigator>
			</View>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	container: {
		flex: 1,
		backgroundColor: colors.canvas,
	},
	headingContainer: {
		paddingHorizontal: space.gutter,
		paddingTop: 20,
		paddingBottom: space.md,
	},
	tabContainer: {
		flex: 1,
	},
	tabScene: {
		backgroundColor: colors.canvas,
	},
	infoScrollView: {
		flex: 1,
		backgroundColor: colors.canvas,
	},
	infoContent: {
		paddingHorizontal: space.gutter,
		paddingTop: space.xs,
		paddingBottom: space.xl,
		gap: space.lg,
	},
	label: {
		...type.fieldLabel,
		color: colors.text,
		marginTop: space.md,
		marginBottom: 6,
	},
	// Full-width field with room before the contact details under it.
	selectorButton: {
		marginBottom: space.lg,
	},
	required: {
		color: colors.danger,
	},
	helperText: {
		...type.secondary,
		color: colors.textSecondary,
		marginBottom: space.sm,
	},
	emailTemplateButton: {
		marginTop: space.sm,
	},
	emailDialogContent: {
		width: '100%',
		maxHeight: '80%',
	},
	emailDialogTitle: {
		...type.cardTitle,
		color: colors.text,
		marginBottom: space.md,
		paddingRight: 36,
	},
	emailBodyScroll: {
		maxHeight: 320,
		borderWidth: 1,
		borderColor: colors.border,
		borderRadius: radius.card,
		marginBottom: space.md,
		backgroundColor: colors.canvas,
	},
	emailBodyScrollContent: {
		padding: space.md,
	},
	emailBodyText: {
		...type.secondary,
		lineHeight: 20,
		color: colors.text,
	},
	listWrapper: {
		flex: 1,
		backgroundColor: colors.canvas,
	},
	placeholderText: {
		...type.secondary,
		color: colors.textSecondary,
		padding: space.lg,
	},
	listButton: {
		marginLeft: space.sm,
	},
});

export default compose(DetailContainer)(LocationSetupDetail);
